import React from "react";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import DrawerHeader from "./DrawerHeader";
import DrawerFooterContainerLarge from "./DrawerFooterContainerLarge";
import ButtonOutlined from "./ButtonOutlined";
import ButtonDelete from "./ButtonDelete";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import dayjs from "dayjs";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { formatDateReadable } from "../../utilities/helpers";
import { toast } from "react-toastify";
import { Lightbox } from "react-modal-image";
import { CERTIFICATE_TYPE_LABELS } from "../../../../../utilities/certificateTypes";

const DEFAULT_DATA = {
  expiration_date: "",
  file: "",
  type: "",
  uuid: null,
};

function isPdfFile(file, previewUrl) {
  if (file && typeof file === "object") {
    if (file.type === "application/pdf") return true;
    if (typeof file.name === "string" && file.name.toLowerCase().endsWith(".pdf")) return true;
  }
  if (typeof previewUrl === "string" && previewUrl) {
    const path = previewUrl.split("?")[0].toLowerCase();
    if (path.endsWith(".pdf")) return true;
  }
  return false;
}

function CertificateDrawer({ open, onClose, certificateData, onSave, onDelete, isEdit, employees, certificateTypes }) {
  const [modalData, setModalData] = React.useState(DEFAULT_DATA);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = React.useState(null);
  const [isFileLoading, setIsFileLoading] = React.useState(false);
  const [showDailyDatePicker, setShowDailyDatePicker] = React.useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState("");
  const [fileRemoved, setFileRemoved] = React.useState(false);
  const [showLightbox, setShowLightbox] = React.useState(false);
  const [lightboxImage, setLightboxImage] = React.useState(null);
  const [pdfPreviewError, setPdfPreviewError] = React.useState(false);
  const datePickerRef = React.useRef(null);

  // Sync state when drawer opens with new data
  React.useEffect(() => {
    if (open) {
      if (certificateData) {
        setModalData(certificateData);
        // If the file is a URL string (existing certificate), use it as the preview
        if (typeof certificateData.file === "string" && certificateData.file) {
          setFilePreviewUrl(certificateData.file);
          setSelectedFile({ name: certificateData.file.split("/").pop()?.split("?")[0] || "Certificate" });
        } else {
          setSelectedFile(null);
          setFilePreviewUrl(null);
        }
      } else {
        setModalData(DEFAULT_DATA);
        setSelectedFile(null);
        setFilePreviewUrl(null);
      }
      setSelectedEmployeeId(certificateData?.prefillEmployeeId || "");
      setFileRemoved(false);
      setShowDailyDatePicker(false);
    }
  }, [open, certificateData]);

  // Clean up object URLs on unmount
  React.useEffect(() => {
    return () => {
      if (filePreviewUrl && filePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  React.useEffect(() => {
    setPdfPreviewError(false);
  }, [filePreviewUrl]);

  // Close date picker on outside click
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDailyDatePicker(false);
      }
    }

    if (showDailyDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDailyDatePicker]);

  // Uploadable document types from the company catalog (falls back to the built-in
  // uploadable set when the catalog hasn't loaded). emergency_contact is excluded
  // upstream because it is not uploadable; types hidden from the Documents overview
  // are excluded here too, so you can't add a type the company has turned off (an
  // existing doc of such a type stays editable via the current-value injection below).
  const uploadableTypeOptions = React.useMemo(() => {
    if (Array.isArray(certificateTypes) && certificateTypes.length > 0) {
      return certificateTypes.filter((t) => t.uploadable && !t.overview_hidden);
    }
    return [
      { value: "food_permit", label: "Food Permit", has_expiration: true },
      { value: "liquor_permit", label: "Liquor Permit", has_expiration: true },
      { value: "break_waiver", label: "Meal Period Waiver", has_expiration: false },
      { value: "other", label: "Other", has_expiration: true },
    ];
  }, [certificateTypes]);

  // When editing a certificate whose type is no longer offered (an archived custom
  // type, or break_waiver after the store flag was disabled), keep it visible in the
  // dropdown so the Select isn't blank and the value isn't silently changed.
  const selectableTypeOptions = React.useMemo(() => {
    const current = modalData?.type;
    if (!current || uploadableTypeOptions.some((t) => t.value === current)) {
      return uploadableTypeOptions;
    }
    const currentLabel =
      (Array.isArray(certificateTypes) &&
        certificateTypes.find((t) => t.value === current)?.label) ||
      CERTIFICATE_TYPE_LABELS[current] ||
      current;
    return [
      ...uploadableTypeOptions,
      { value: current, label: currentLabel, has_expiration: true },
    ];
  }, [uploadableTypeOptions, certificateTypes, modalData?.type]);

  const selectedTypeHasExpiration = React.useMemo(() => {
    const match = selectableTypeOptions.find((t) => t.value === modalData?.type);
    // Default to showing the field for unknown/unselected types.
    return match ? match.has_expiration !== false : true;
  }, [selectableTypeOptions, modalData?.type]);

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    const match = uploadableTypeOptions.find((t) => t.value === newType);
    setModalData((prev) => ({
      ...prev,
      type: newType,
      // Clear a stale expiration when switching to a type that doesn't track one.
      expiration_date:
        match && match.has_expiration === false ? "" : prev.expiration_date,
    }));
  };

  const handleDateChange = (newValue) => {
    const formattedDate = newValue ? newValue.format("YYYY-MM-DD") : "";
    setModalData((prev) => ({ ...prev, expiration_date: formattedDate }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsFileLoading(true);

    // Revoke old blob URL if exists
    if (filePreviewUrl && filePreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(filePreviewUrl);
    }

    setSelectedFile(file);
    setModalData((prev) => ({ ...prev, file: file }));
    setFileRemoved(false);
    setPdfPreviewError(false);

    // Create local preview URL
    const previewUrl = URL.createObjectURL(file);
    setFilePreviewUrl(previewUrl);
    setIsFileLoading(false);

    // Reset input so re-selecting the same file triggers change
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    if (filePreviewUrl && filePreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setModalData((prev) => ({ ...prev, file: "" }));
    setFileRemoved(true);
  };

  const handleClose = () => {
    setModalData(DEFAULT_DATA);
    setSelectedFile(null);
    setFilePreviewUrl(null);
    setShowDailyDatePicker(false);
    setShowLightbox(false);
    onClose();
  };

  const handleSubmit = () => {
    // Require employee selection when creating from store-wide view
    if (!isEdit && employees && employees.length > 0 && !selectedEmployeeId) {
      toast.error("Please select an employee");
      return;
    }

    const employeeId = selectedEmployeeId || certificateData?.prefillEmployeeId || null;
    onSave(
      modalData.uuid || null,
      modalData.type,
      modalData.expiration_date || null,
      modalData.file || null,
      employeeId,
      fileRemoved
    );
    handleClose();
  };

  const handleDelete = () => {
    if (onDelete && modalData.uuid) {
      if (!window.confirm("Are you sure you want to delete this certificate? This action cannot be undone.")) {
        return;
      }
      onDelete(modalData.uuid);
      handleClose();
    }
  };

  return (
    <Drawer open={open} anchor="right" onClose={handleClose}>
      <div className="w-[450px] h-full">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={isEdit ? "Edit Certificate" : "Add Certificate"}
            onClose={handleClose}
          />
          <div className="w-full px-6">
            {/* Employee selector — only shown when adding from store-wide view */}
            {!isEdit && employees && employees.length > 0 && (
              <div className="mt-5">
                <p className="text-sm text-slate-500">Employee</p>
                <div className="mt-1">
                  <Select
                    fullWidth
                    displayEmpty
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      paddingY: "1px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      height: "45px",
                      backgroundColor: "white",
                    }}
                  >
                    <MenuItem disabled value="">
                      Select employee
                    </MenuItem>
                    {employees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            <div className="mt-5">
              <p className="text-sm text-slate-500">Type</p>
              <div className="mt-1">
                <Select
                  fullWidth
                  displayEmpty
                  value={modalData?.type}
                  onChange={handleTypeChange}
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    paddingY: "1px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                    height: "45px",
                    backgroundColor: "white",
                  }}
                >
                  <MenuItem disabled value="">
                    Select certificate type
                  </MenuItem>
                  {selectableTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
            {selectedTypeHasExpiration && (
            <div className="mt-5">
              <p className="text-sm text-slate-500">Expiration Date</p>
              <div
                className={`${
                  !showDailyDatePicker ? "border border-white" : ""
                } flex-1 mt-1`}
              >
                <div
                  className={`flex-1 bg-white rounded-lg text-sm ${
                    showDailyDatePicker
                      ? "border-2 border-violet-700"
                      : "border border-[#bdbdbd] hover:border-slate-900"
                  } cursor-pointer relative`}
                  ref={datePickerRef}
                >
                  <div
                    className="w-full h-full px-3.5 py-2.5"
                    onClick={() => setShowDailyDatePicker((prev) => !prev)}
                  >
                    <div className="w-full h-full flex items-center justify-between">
                      <p className="text-[16px]">
                        {modalData.expiration_date
                          ? formatDateReadable(modalData.expiration_date)
                          : "Click to select"}
                      </p>
                      <div className="text-slate-500">
                        <CalendarTodayIcon
                          style={{ fontSize: "18px", marginBottom: "2px" }}
                        />
                      </div>
                    </div>
                  </div>
                  {showDailyDatePicker && (
                    <div className="absolute top-10 left-0 bg-white z-10 flex items-center justify-center pl-4 pr-5 border border-[#bdbdbd] rounded-lg shadow-xl overflow-hidden">
                      <div className="mt-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer
                            components={["DateCalendar", "DateCalendar"]}
                          >
                            <DemoItem>
                              <DateCalendar
                                value={
                                  modalData.expiration_date
                                    ? dayjs(modalData.expiration_date)
                                    : null
                                }
                                onChange={handleDateChange}
                                slotProps={{
                                  day: {
                                    sx: {
                                      "&.MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: "#6d28d9",
                                      },
                                    },
                                  },
                                }}
                              />
                            </DemoItem>
                          </DemoContainer>
                        </LocalizationProvider>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
            {/* Attachment */}
            <div className="mt-5">
              <p className="text-sm text-slate-500">Attachment</p>
              <div className="mt-1">
                {/* Thumbnail preview */}
                {filePreviewUrl && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    <div
                      className="relative group w-[160px] h-[160px] rounded-lg border border-slate-200 overflow-hidden bg-white cursor-pointer"
                      onClick={() => {
                        if (isPdfFile(selectedFile, filePreviewUrl)) {
                          window.open(filePreviewUrl, "_blank");
                        } else {
                          setLightboxImage(filePreviewUrl);
                          setShowLightbox(true);
                        }
                      }}
                    >
                      {isPdfFile(selectedFile, filePreviewUrl) ? (
                        pdfPreviewError ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-red-50">
                            <PictureAsPdfIcon sx={{ fontSize: 48, color: "#ef4444" }} />
                            <span className="mt-2 text-xs text-slate-600">PDF</span>
                          </div>
                        ) : (
                          <iframe
                            src={filePreviewUrl}
                            title="Document PDF"
                            className="w-full h-full border-0 pointer-events-none"
                            onError={() => setPdfPreviewError(true)}
                          />
                        )
                      ) : (
                        <img
                          src={filePreviewUrl}
                          alt="Document"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile();
                          }}
                          sx={{
                            backgroundColor: "rgba(0,0,0,0.5)",
                            color: "white",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                            width: 24,
                            height: 24,
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                )}
                {/* No preview placeholder */}
                {!filePreviewUrl && !isFileLoading && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    <div className="w-[160px] h-[160px] rounded-lg border border-dashed border-slate-300 overflow-hidden bg-slate-50 flex items-center justify-center">
                      <ImageIcon sx={{ color: "#94a3b8", fontSize: 32 }} />
                    </div>
                  </div>
                )}
                {/* Attach file link */}
                <label className="flex items-center gap-2 text-violet-700 cursor-pointer hover:text-violet-800">
                  {isFileLoading ? (
                    <CircularProgress size={20} sx={{ color: "#6d28d9" }} />
                  ) : (
                    <AttachFileIcon fontSize="small" />
                  )}
                  <span className="text-sm">
                    {isFileLoading
                      ? "Uploading..."
                      : filePreviewUrl
                        ? "Change File"
                        : "Attach File"}
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={handleFileChange}
                    disabled={isFileLoading}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="h-[400px]"></div>
        </div>
        <div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton
                variant="contained"
                loadingPosition="end"
                endIcon={<SaveIcon />}
                onClick={handleSubmit}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": {
                    backgroundColor: "#5b21b6",
                  },
                  fontSize: "16px",
                }}
              >
                {isEdit ? "Save Changes" : "Save Certificate"}
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              {isEdit && onDelete ? (
                <ButtonDelete text="Delete" onClick={handleDelete} />
              ) : (
                <ButtonOutlined text="Cancel" onClick={handleClose} />
              )}
            </div>
          </DrawerFooterContainerLarge>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && lightboxImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={lightboxImage}
            medium={lightboxImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
    </Drawer>
  );
}

export default CertificateDrawer;
